import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  fetchReunionesRanking,
  fetchReunionesInforme,
  fetchReunionesList,
  fetchActiveSellers,
  createReunion,
  updateReunion,
  deleteReunion
} from '../../Redux/Actions/actions';
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, PointElement, LineElement,
  BarElement, ArcElement, Tooltip, Legend, Filler
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import styles from './estadisticas.module.css';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, ArcElement, Tooltip, Legend, Filler);

const MESES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];

const currentYear = new Date().getFullYear();
const currentMonthName = MESES[new Date().getMonth()];
const todayStr = new Date().toISOString().slice(0, 10);

// Quincenal auto-detection helper
const getQuincenalRange = () => {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth(); // 0-based
  const day = now.getDate();
  const pad = (n) => String(n).padStart(2, '0');
  if (day <= 15) {
    return {
      desde: `${y}-${pad(m + 1)}-01`,
      hasta: `${y}-${pad(m + 1)}-15`
    };
  }
  const lastDay = new Date(y, m + 1, 0).getDate();
  return {
    desde: `${y}-${pad(m + 1)}-16`,
    hasta: `${y}-${pad(m + 1)}-${pad(lastDay)}`
  };
};

// Score formula
const calcScore = (v) => {
  const ventas = Number(v.ventasCerradas) || 0;
  const visitas = Number(v.visitasRealizadas) || 0;
  const vrc = Number(v.tasaciones) || 0;
  const captadas = Number(v.propiedadesCaptadas) || 0;
  const posteos = Number(v.posteosIG) || 0;
  return ventas * 3 + visitas * 2 + vrc * 1.5 + captadas * 2 + posteos * 0.5;
};

const calcConversion = (v) => {
  const ventas = Number(v.ventasCerradas) || 0;
  const visitas = Number(v.visitasRealizadas) || 0;
  return ventas > 0 && visitas > 0 ? ((ventas / visitas) * 100).toFixed(1) : null;
};

const calcCumplimiento = (v) => {
  const dados = Number(v.compromisosDados) || 0;
  const cumplidos = Number(v.compromisosCumplidos) || 0;
  return dados > 0 ? ((cumplidos / dados) * 100).toFixed(1) : null;
};

const formatARS = (n) => {
  if (n == null) return '—';
  return '$' + Number(n).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatDate = (d) => {
  if (!d) return '—';
  const parts = d.split('-');
  if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
  return d;
};

// ============ REUNION FORM MODAL ============
const ReunionFormModal = ({ isOpen, onClose, onSave, activeSellers, editData, preSelectedSellerId, loading }) => {
  const defaultRange = getQuincenalRange();

  const emptyForm = useMemo(() => ({
    sellerId: preSelectedSellerId || '',
    mes: currentMonthName,
    fecha: todayStr,
    rangoDesde: defaultRange.desde,
    rangoHasta: defaultRange.hasta,
    ventasCerradas: '',
    montoVendido: '',
    visitasRealizadas: '',
    tasaciones: '',
    propiedadesCaptadas: '',
    posteosIG: '',
    compromisosDados: '',
    compromisosCumplidos: '',
    notas: ''
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [preSelectedSellerId]);

  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState('');
  const [precargaData, setPrecargaData] = useState(null);
  const [precargaLoading, setPrecargaLoading] = useState(false);
  const [precargaError, setPrecargaError] = useState('');

  useEffect(() => {
    if (isOpen) {
      if (editData) {
        setForm({
          sellerId: editData.sellerId || '',
          mes: editData.mes || currentMonthName,
          fecha: editData.fecha || todayStr,
          rangoDesde: editData.rangoDesde || defaultRange.desde,
          rangoHasta: editData.rangoHasta || defaultRange.hasta,
          ventasCerradas: editData.ventasCerradas ?? '',
          montoVendido: editData.montoVendido ?? '',
          visitasRealizadas: editData.visitasRealizadas ?? '',
          tasaciones: editData.tasaciones ?? '',
          propiedadesCaptadas: editData.propiedadesCaptadas ?? '',
          posteosIG: editData.posteosIG ?? '',
          compromisosDados: editData.compromisosDados ?? '',
          compromisosCumplidos: editData.compromisosCumplidos ?? '',
          notas: editData.notas || ''
        });
        setPrecargaData(editData.datosPrecargados || null);
      } else {
        setForm({ ...emptyForm, sellerId: preSelectedSellerId || '' });
        setPrecargaData(null);
      }
      setError('');
      setPrecargaError('');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, editData, preSelectedSellerId, emptyForm]);

  const handleChange = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  // Precarga: fetch when sellerId + rangoDesde + rangoHasta are ready
  const fetchPrecarga = useCallback(async () => {
    if (!form.sellerId || !form.rangoDesde || !form.rangoHasta) return;
    setPrecargaLoading(true);
    setPrecargaError('');
    try {
      const res = await axios.get(`/reuniones/precarga/${encodeURIComponent(form.sellerId)}`, {
        params: { desde: form.rangoDesde, hasta: form.rangoHasta }
      });
      const data = res.data?.data || res.data;
      setPrecargaData(data);
      // Auto-fill form from resumen
      if (data?.resumen) {
        setForm(prev => ({
          ...prev,
          visitasRealizadas: data.resumen.visitasRealizadas ?? prev.visitasRealizadas,
          propiedadesCaptadas: data.resumen.propiedadesCaptadas ?? prev.propiedadesCaptadas,
          ventasCerradas: data.resumen.ventasCerradas ?? prev.ventasCerradas,
          montoVendido: data.resumen.montoVendido ?? prev.montoVendido
        }));
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Error al obtener precarga';
      setPrecargaError(msg);
      setPrecargaData(null);
    } finally {
      setPrecargaLoading(false);
    }
  }, [form.sellerId, form.rangoDesde, form.rangoHasta]);

  const scorePreview = calcScore(form);
  const convPreview = calcConversion(form);
  const cumpPreview = calcCumplimiento(form);

  const handleSubmit = async () => {
    if (!form.sellerId || !form.mes || !form.fecha) {
      setError('Completá agente, mes y fecha');
      return;
    }
    setError('');
    const body = {
      sellerId: Number(form.sellerId),
      mes: form.mes,
      fecha: form.fecha,
      rangoDesde: form.rangoDesde || undefined,
      rangoHasta: form.rangoHasta || undefined,
      ventasCerradas: Number(form.ventasCerradas) || 0,
      montoVendido: Number(form.montoVendido) || 0,
      visitasRealizadas: Number(form.visitasRealizadas) || 0,
      tasaciones: Number(form.tasaciones) || 0,
      propiedadesCaptadas: Number(form.propiedadesCaptadas) || 0,
      posteosIG: Number(form.posteosIG) || 0,
      compromisosDados: Number(form.compromisosDados) || 0,
      compromisosCumplidos: Number(form.compromisosCumplidos) || 0,
      notas: form.notas,
      datosPrecargados: precargaData || undefined
    };
    try {
      await onSave(body, editData?.id);
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Error del servidor';
      setError(msg);
    }
  };

  if (!isOpen) return null;

  const sellers = activeSellers?.data || activeSellers || [];
  const isEdit = !!editData;

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={e => e.stopPropagation()} style={{ maxWidth: 680 }}>
        <h2 className={styles.modalTitle}>{isEdit ? 'Editar Reunión' : 'Nueva Reunión Individual'}</h2>

        {error && <div className={styles.formError}>{error}</div>}

        {/* Sección: Datos generales + Rango de estudio */}
        <div className={styles.formSectionLabel}>Datos generales</div>
        <div className={styles.formGrid}>
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Agente *</label>
            <select className={styles.formInput} value={form.sellerId}
              onChange={e => handleChange('sellerId', e.target.value)}
              disabled={isEdit}>
              <option value="">Seleccionar agente</option>
              {sellers.map(s => (
                <option key={s.id} value={s.id}>
                  {s.name} {s.last_name || s.lastName || ''}
                </option>
              ))}
            </select>
          </div>
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Mes *</label>
            <select className={styles.formInput} value={form.mes}
              onChange={e => handleChange('mes', e.target.value)}>
              <option value="">Seleccionar mes</option>
              {MESES.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Fecha *</label>
            <input type="date" className={styles.formInput} value={form.fecha}
              onChange={e => handleChange('fecha', e.target.value)} />
          </div>
        </div>

        {/* Rango de estudio (quincenal) */}
        <div className={styles.formSectionLabel}>Rango de estudio <span style={{ fontWeight: 400, fontSize: 11, color: '#6B6B8A' }}>(quincena auto-detectada, editable)</span></div>
        <div className={styles.formRangeRow}>
          <div className={styles.formGroup} style={{ flex: 1 }}>
            <label className={styles.formLabel}>Desde</label>
            <input type="date" className={styles.formInput} value={form.rangoDesde}
              onChange={e => handleChange('rangoDesde', e.target.value)} />
          </div>
          <div className={styles.formGroup} style={{ flex: 1 }}>
            <label className={styles.formLabel}>Hasta</label>
            <input type="date" className={styles.formInput} value={form.rangoHasta}
              onChange={e => handleChange('rangoHasta', e.target.value)} />
          </div>
          <button type="button" className={styles.btnPrecarga}
            onClick={fetchPrecarga}
            disabled={precargaLoading || !form.sellerId || !form.rangoDesde || !form.rangoHasta}>
            {precargaLoading ? '⏳ Cargando...' : '🔄 Precargar datos'}
          </button>
        </div>
        {precargaError && <div className={styles.formError} style={{ marginTop: 4 }}>{precargaError}</div>}
        {precargaData && !precargaLoading && (
          <div className={styles.precargaInfo}>
            ✅ Datos precargados ({form.rangoDesde} → {form.rangoHasta}): {precargaData.resumen?.visitasRealizadas ?? 0} visitas · {precargaData.resumen?.propiedadesCaptadas ?? 0} captadas · {precargaData.resumen?.ventasCerradas ?? 0} ventas · {precargaData.resumen?.operacionesExternas || 0} op. externas · {formatARS(precargaData.resumen?.montoVendido)} vendido · {formatARS(precargaData.resumen?.montoOperacionesExternas)} op. ext.
          </div>
        )}
        {precargaData && !precargaLoading && precargaData.operacionesExternas?.detalle?.length > 0 && (
          <div className={styles.precargaInfo} style={{ marginTop: 4, background: '#FDE8EC' }}>
            🤝 {precargaData.operacionesExternas.total} op. externas en el período — {formatARS(precargaData.operacionesExternas.montoTotal)} total
            <ul style={{ margin: '4px 0 0 16px', fontSize: 12 }}>
              {precargaData.operacionesExternas.detalle.map((op, i) => (
                <li key={i}>{op.titulo} — {op.currencyCierre} {op.precioCierre}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Métricas numéricas */}
        <div className={styles.formSectionLabel} style={{ marginTop: 16 }}>Métricas</div>
        <div className={styles.formGrid}>
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Ventas cerradas</label>
            <input type="number" min="0" className={styles.formInput} placeholder="0"
              value={form.ventasCerradas} onChange={e => handleChange('ventasCerradas', e.target.value)} />
          </div>
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Monto vendido ($)</label>
            <input type="number" min="0" step="0.01" className={styles.formInput} placeholder="0.00"
              value={form.montoVendido} onChange={e => handleChange('montoVendido', e.target.value)} />
          </div>
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Visitas realizadas</label>
            <input type="number" min="0" className={styles.formInput} placeholder="0"
              value={form.visitasRealizadas} onChange={e => handleChange('visitasRealizadas', e.target.value)} />
          </div>
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>VRC (Tasaciones)</label>
            <input type="number" min="0" className={styles.formInput} placeholder="0"
              value={form.tasaciones} onChange={e => handleChange('tasaciones', e.target.value)} />
          </div>
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Props. captadas</label>
            <input type="number" min="0" className={styles.formInput} placeholder="0"
              value={form.propiedadesCaptadas} onChange={e => handleChange('propiedadesCaptadas', e.target.value)} />
          </div>
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Posteos Instagram</label>
            <input type="number" min="0" className={styles.formInput} placeholder="0"
              value={form.posteosIG} onChange={e => handleChange('posteosIG', e.target.value)} />
          </div>
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Compromisos dados</label>
            <input type="number" min="0" className={styles.formInput} placeholder="0"
              value={form.compromisosDados} onChange={e => handleChange('compromisosDados', e.target.value)} />
          </div>
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Compromisos cumplidos</label>
            <input type="number" min="0" className={styles.formInput} placeholder="0"
              value={form.compromisosCumplidos} onChange={e => handleChange('compromisosCumplidos', e.target.value)} />
          </div>
        </div>

        <div className={styles.formGroupFull}>
          <label className={styles.formLabel}>Notas / Resumen de la reunión</label>
          <textarea className={styles.formTextarea} rows={3}
            placeholder="Observaciones, compromisos, feedback..."
            value={form.notas} onChange={e => handleChange('notas', e.target.value)} />
        </div>

        {/* Live preview */}
        <div className={styles.formPreview}>
          <div className={`${styles.previewBadge} ${styles.previewBadgeLarge}`}>
            Score: {scorePreview.toFixed(1)}
          </div>
          {convPreview !== null && (
            <div className={`${styles.previewBadge} ${Number(convPreview) >= 50 ? styles.previewBadgeGreen : styles.previewBadgeOrange}`}>
              Conv: {convPreview}%
            </div>
          )}
          {cumpPreview !== null && (
            <div className={`${styles.previewBadge} ${Number(cumpPreview) >= 80 ? styles.previewBadgeGreen : Number(cumpPreview) >= 50 ? styles.previewBadgeOrange : styles.previewBadgeRed}`}>
              Comp: {cumpPreview}%
            </div>
          )}
        </div>

        <div className={styles.formActions}>
          {isEdit && editData?.id && (
            <button className={styles.btnDelete} type="button" disabled>Eliminar</button>
          )}
          <button className={styles.btnCancel} onClick={onClose} type="button">Cancelar</button>
          <button className={styles.btnSubmit} onClick={handleSubmit} disabled={loading} type="button">
            {loading ? 'Guardando...' : isEdit ? 'Actualizar reunión' : 'Guardar reunión'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============ MAIN COMPONENT ============
const ReunionesStats = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Redux state
  const {
    reunionesRanking, reunionesInforme, reunionesList,
    activeSellers, estadisticasLoading, reunionCrudLoading
  } = useSelector(state => state);

  // Views: 'selector' | 'ranking' | 'informe'
  const [view, setView] = useState('selector');
  const [year, setYear] = useState(currentYear);
  const [orderBy, setOrderBy] = useState('score');
  const [selectedAgent, setSelectedAgent] = useState(null);

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [preSelectedSellerId, setPreSelectedSellerId] = useState(null);

  // Toast
  const [toast, setToast] = useState(null);

  // Confirm delete
  const [confirmDelete, setConfirmDelete] = useState(null);

  // Load active sellers on mount
  useEffect(() => {
    dispatch(fetchActiveSellers());
  }, [dispatch]);

  // Load ranking when on ranking view
  useEffect(() => {
    if (view === 'ranking') {
      dispatch(fetchReunionesRanking({ year, orderBy }));
    }
  }, [dispatch, view, year, orderBy]);

  // Load informe when agent selected
  useEffect(() => {
    if (selectedAgent && view === 'informe') {
      dispatch(fetchReunionesInforme(selectedAgent, year));
      dispatch(fetchReunionesList({ sellerId: selectedAgent, limit: 50 }));
    }
  }, [dispatch, selectedAgent, view, year]);

  // Auto-dismiss toast
  useEffect(() => {
    if (toast) {
      const t = setTimeout(() => setToast(null), 3000);
      return () => clearTimeout(t);
    }
  }, [toast]);

  const showToast = (msg, type = 'success') => setToast({ msg, type });

  const handleBack = () => {
    if (view === 'informe') { setView('ranking'); setSelectedAgent(null); }
    else if (view === 'ranking') setView('selector');
    else navigate('/estadisticas');
  };

  const goToInforme = useCallback((sellerId) => {
    setSelectedAgent(sellerId);
    setView('informe');
  }, []);

  const openNewReunion = (sellerId = null) => {
    setEditData(null);
    setPreSelectedSellerId(sellerId);
    setModalOpen(true);
  };

  const openEditReunion = (reunion) => {
    setEditData(reunion);
    setPreSelectedSellerId(null);
    setModalOpen(true);
  };

  const handleSaveReunion = async (body, editId) => {
    if (editId) {
      await dispatch(updateReunion(editId, body));
      showToast('Reunión actualizada exitosamente');
    } else {
      await dispatch(createReunion(body));
      showToast('Reunión cargada exitosamente');
    }
    setModalOpen(false);
    // Refresh data
    if (view === 'ranking') dispatch(fetchReunionesRanking({ year, orderBy }));
    if (view === 'informe' && selectedAgent) {
      dispatch(fetchReunionesInforme(selectedAgent, year));
      dispatch(fetchReunionesList({ sellerId: selectedAgent, limit: 50 }));
    }
  };

  const handleDeleteReunion = async (id) => {
    try {
      await dispatch(deleteReunion(id));
      showToast('Reunión eliminada exitosamente');
      setConfirmDelete(null);
      if (selectedAgent) {
        dispatch(fetchReunionesInforme(selectedAgent, year));
        dispatch(fetchReunionesList({ sellerId: selectedAgent, limit: 50 }));
      }
    } catch {
      showToast('Error al eliminar', 'error');
    }
  };

  // ============ INFORME DATA (always computed, never conditionally) ============
  const informe = reunionesInforme?.data || reunionesInforme || {};
  const agente = informe.agente || {};
  const kpis = informe.kpis || {};
  const compromisos = informe.compromisos || {};
  const evolucion = informe.evolucionMensual || informe.evolucion || [];
  const comparativa = informe.comparativaEquipo || {};
  const mejorMes = informe.mejorMes || null;
  const peorMes = informe.peorMes || null;
  const notasPorMes = informe.notasPorMes || [];
  const tips = informe.tips || [];
  const frase = informe.fraseMotivacional || '';
  const totalReuniones = informe.totalReuniones || 0;
  const mesesActivos = informe.mesesActivos || 0;
  const periodo = informe.periodo || year;
  const historial = reunionesList?.data || [];
  const nombreAgente = `${agente.name || ''} ${agente.last_name || ''}`.trim() || 'Agente';
  const initialAgente = nombreAgente[0]?.toUpperCase() || '?';

  // Ranking data
  const rankingData = reunionesRanking?.data || reunionesRanking || {};
  const agentes = rankingData.ranking || rankingData.agentes || [];
  const promEquipo = rankingData.promedioEquipo || {};
  const promScore = promEquipo.score || 0;

  const scoreColor = useCallback((score) => {
    if (!promScore) return '#1A1A3E';
    if (score > promScore) return '#27AE60';
    if (score < promScore) return '#E74C3C';
    return '#1A1A3E';
  }, [promScore]);

  // Line chart data
  const lineData = useMemo(() => ({
    labels: evolucion.map(e => e.mes),
    datasets: [
      {
        label: 'Score',
        data: evolucion.map(e => e.score || 0),
        borderColor: '#2E2F8F',
        backgroundColor: 'rgba(46,47,143,0.08)',
        fill: true, tension: 0.3, borderWidth: 2.5,
        pointRadius: 5, pointBackgroundColor: '#2E2F8F'
      },
      {
        label: 'Ventas',
        data: evolucion.map(e => e.ventasCerradas || 0),
        borderColor: '#8B2252',
        borderDash: [4, 3],
        pointRadius: 4, pointBackgroundColor: '#8B2252',
        fill: false, tension: 0.3, borderWidth: 2
      }
    ]
  }), [evolucion]);

  const lineOpts = {
    responsive: true,
    scales: {
      y: { beginAtZero: true, grid: { color: '#F0F0F8' } },
      x: { grid: { display: false } }
    },
    plugins: { legend: { position: 'bottom' } }
  };

  // Bar chart: comparativa
  const compAgente = comparativa.agente || {};
  const compEquipo = comparativa.promedioEquipo || {};
  const compKeys = ['ventasCerradas', 'visitasRealizadas', 'tasaciones', 'propiedadesCaptadas'];
  const compLabelsMap = { ventasCerradas: 'Ventas', visitasRealizadas: 'Visitas', tasaciones: 'VRC', propiedadesCaptadas: 'Captadas' };

  const barData = useMemo(() => ({
    labels: compKeys.map(k => compLabelsMap[k]),
    datasets: [
      { label: nombreAgente, data: compKeys.map(k => compAgente[k] || 0), backgroundColor: '#2E2F8F', borderRadius: 6 },
      { label: 'Promedio equipo', data: compKeys.map(k => compEquipo[k] || 0), backgroundColor: '#E8E8F8', borderColor: '#2E2F8F', borderWidth: 1, borderRadius: 6 }
    ]
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [compAgente, compEquipo, nombreAgente]);

  const barOpts = {
    responsive: true,
    plugins: { legend: { position: 'bottom' } },
    scales: {
      y: { beginAtZero: true, grid: { color: '#F0F0F8' } },
      x: { grid: { display: false } }
    }
  };

  // Donut: compromisos
  const compCumplidos = compromisos.cumplidos || 0;
  const compPendientes = compromisos.pendientes || 0;
  const compDados = compromisos.dados || (compCumplidos + compPendientes);
  const compPorcentaje = compromisos.porcentaje || (compDados > 0 ? ((compCumplidos / compDados) * 100).toFixed(0) : 0);

  const donutData = useMemo(() => ({
    labels: ['Cumplidos', 'Pendientes'],
    datasets: [{
      data: [compCumplidos, compPendientes],
      backgroundColor: ['#27AE60', '#F5E6EC'],
      borderWidth: 0, spacing: 2
    }]
  }), [compCumplidos, compPendientes]);

  const donutOpts = { responsive: true, cutout: '70%', plugins: { legend: { display: false } } };

  // ============ PANTALLA 1: SELECTOR ============
  if (view === 'selector') {
    const sellers = activeSellers?.data || activeSellers || [];

    return (
      <div className={styles.dashboardContainer}>
        <div className={styles.dashboardHeader}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <button className={styles.backButton} onClick={() => navigate('/estadisticas')}>← Volver</button>
            <div>
              <h1 className={styles.dashboardTitle}>Reuniones Individuales</h1>
              <p className={styles.sectionSub} style={{ marginBottom: 0 }}>Directorio de Agentes · Gestión {year}</p>
            </div>
          </div>
          <div className={styles.headerRight}>
            <button className={styles.filterButton} onClick={() => setView('ranking')}>Ver Ranking</button>
            <button className={styles.btnNewReunion} onClick={() => openNewReunion()}>+ Nueva Reunión</button>
          </div>
        </div>

        {estadisticasLoading && !sellers.length ? (
          <div className={styles.loadingContainer}><div className={styles.spinner} /><span>Cargando agentes...</span></div>
        ) : sellers.length === 0 ? (
          <div className={styles.emptyState}><span className={styles.emptyIcon}>👥</span><p>No hay agentes activos</p></div>
        ) : (
          <div className={styles.agentGrid}>
            {sellers.map(s => (
              <div key={s.id} className={styles.agentCard} onClick={() => goToInforme(s.id)}>
                <div className={styles.agentAvatar}>
                  {(s.name || '?')[0].toUpperCase()}
                </div>
                <div className={styles.agentName}>{s.name} {s.last_name || s.lastName || ''}</div>
                {s.mail && <div className={styles.agentMail}>{s.mail}</div>}
              </div>
            ))}
          </div>
        )}

        <ReunionFormModal isOpen={modalOpen} onClose={() => setModalOpen(false)}
          onSave={handleSaveReunion} activeSellers={activeSellers}
          editData={editData} preSelectedSellerId={preSelectedSellerId}
          loading={reunionCrudLoading} />
        {toast && <div className={`${styles.toast} ${toast.type === 'error' ? styles.toastError : styles.toastSuccess}`}>{toast.msg}</div>}
      </div>
    );
  }

  // ============ PANTALLA 2: RANKING ============
  if (view === 'ranking') {
    return (
      <div className={styles.dashboardContainer}>
        <div className={styles.dashboardHeader}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <button className={styles.backButton} onClick={handleBack}>← Volver</button>
            <h1 className={styles.dashboardTitle}>Ranking de Agentes {year}</h1>
          </div>
          <div className={styles.headerRight}>
            <div className={styles.filterGroup}>
              <label className={styles.filterLabel}>Año</label>
              <input type="number" className={styles.filterInput} style={{ width: 80 }}
                value={year} onChange={e => setYear(Number(e.target.value))} min={2020} max={2030} />
            </div>
            <div className={styles.filterGroup}>
              <label className={styles.filterLabel}>Ordenar por</label>
              <select className={styles.filterInput} value={orderBy} onChange={e => setOrderBy(e.target.value)}>
                <option value="score">Score</option>
                <option value="ventasCerradas">Ventas</option>
                <option value="visitasRealizadas">Visitas</option>
                <option value="tasaciones">VRC</option>
                <option value="propiedadesCaptadas">Captadas</option>
                <option value="conversionVisitaVenta">Conversión</option>
                <option value="cumplimientoCompromisos">Compromisos</option>
                <option value="posteosIG">Posteos</option>
              </select>
            </div>
            <button className={styles.btnNewReunion} onClick={() => openNewReunion()}>+ Nueva Reunión</button>
            <button className={styles.pdfButton} onClick={() => window.print()}>📄 PDF</button>
          </div>
        </div>

        {estadisticasLoading && !agentes.length ? (
          <div className={styles.loadingContainer}><div className={styles.spinner} /><span>Cargando ranking...</span></div>
        ) : (
          <div className={styles.section}>
            <div className={styles.tableContainer}>
              <table className={styles.dataTable}>
                <thead>
                  <tr>
                    <th>Pos</th>
                    <th>Agente</th>
                    <th>Reun.</th>
                    <th>Ventas</th>
                    <th>Visitas</th>
                    <th>VRC</th>
                    <th>Captadas</th>
                    <th>Conv.</th>
                    <th>Comp.</th>
                    <th>Score</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {agentes.length === 0 ? (
                    <tr><td colSpan={11} style={{ textAlign: 'center', padding: 24, color: '#6B6B8A' }}>No hay datos de reuniones para {year}</td></tr>
                  ) : agentes.map((a, i) => {
                    const nombre = a.nombre || a.fullName || `${a.agente?.name || ''} ${a.agente?.last_name || ''}`.trim() || '—';
                    const initial = nombre[0]?.toUpperCase() || '?';
                    return (
                      <tr key={a.sellerId || i}>
                        <td><span className={styles.medal}>{['🥇','🥈','🥉'][i] || (i + 1)}</span></td>
                        <td>
                          <div className={styles.agentNameCell} onClick={() => goToInforme(a.sellerId)}>
                            <span className={styles.avatarMini}>{initial}</span>
                            {nombre}
                          </div>
                        </td>
                        <td>{a.totalReuniones ?? '—'}</td>
                        <td>{a.ventasCerradas ?? a.ventas ?? '—'}</td>
                        <td>{a.visitasRealizadas ?? a.visitas ?? '—'}</td>
                        <td>{a.tasaciones ?? '—'}</td>
                        <td>{a.propiedadesCaptadas ?? a.captadas ?? '—'}</td>
                        <td>{a.conversionVisitaVenta != null ? `${Number(a.conversionVisitaVenta).toFixed(1)}%` : '—'}</td>
                        <td>{a.cumplimientoCompromisos != null ? `${Number(a.cumplimientoCompromisos).toFixed(1)}%` : '—'}</td>
                        <td style={{ fontWeight: 700, color: scoreColor(a.score || 0) }}>
                          {(a.score || 0).toFixed(1)}
                        </td>
                        <td style={{ display: 'flex', gap: 6 }}>
                          <button className={styles.filterButton} style={{ padding: '4px 10px', fontSize: 11 }}
                            onClick={() => goToInforme(a.sellerId)}>Ver informe</button>
                          <button className={styles.btnNewReunion} style={{ padding: '4px 10px', fontSize: 11 }}
                            onClick={() => openNewReunion(a.sellerId)}>Cargar</button>
                        </td>
                      </tr>
                    );
                  })}
                  {/* Fila promedio equipo */}
                  {Object.keys(promEquipo).length > 0 && (
                    <tr className={styles.promedioRow}>
                      <td></td>
                      <td style={{ fontStyle: 'italic', color: '#6B6B8A' }}>Promedio equipo</td>
                      <td>{promEquipo.totalReuniones?.toFixed(1) ?? '—'}</td>
                      <td>{promEquipo.ventasCerradas?.toFixed(1) ?? promEquipo.ventas?.toFixed(1) ?? '—'}</td>
                      <td>{promEquipo.visitasRealizadas?.toFixed(1) ?? promEquipo.visitas?.toFixed(1) ?? '—'}</td>
                      <td>{promEquipo.tasaciones?.toFixed(1) ?? '—'}</td>
                      <td>{promEquipo.propiedadesCaptadas?.toFixed(1) ?? promEquipo.captadas?.toFixed(1) ?? '—'}</td>
                      <td>{promEquipo.conversionVisitaVenta != null ? `${Number(promEquipo.conversionVisitaVenta).toFixed(1)}%` : '—'}</td>
                      <td>{promEquipo.cumplimientoCompromisos != null ? `${Number(promEquipo.cumplimientoCompromisos).toFixed(1)}%` : '—'}</td>
                      <td style={{ fontWeight: 700, color: '#2E2F8F' }}>{(promEquipo.score || 0).toFixed(1)}</td>
                      <td></td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <ReunionFormModal isOpen={modalOpen} onClose={() => setModalOpen(false)}
          onSave={handleSaveReunion} activeSellers={activeSellers}
          editData={editData} preSelectedSellerId={preSelectedSellerId}
          loading={reunionCrudLoading} />
        {toast && <div className={`${styles.toast} ${toast.type === 'error' ? styles.toastError : styles.toastSuccess}`}>{toast.msg}</div>}
      </div>
    );
  }

  // ============ PANTALLA 3: INFORME INDIVIDUAL ============
  return (
    <div className={styles.dashboardContainer}>
      {/* SECTION 0: Sticky Top Bar */}
      <div className={styles.stickyBar}>
        <div className={styles.stickyBarLeft}>
          <button className={styles.btnOutlineWhite} onClick={handleBack}>← Volver</button>
        </div>
        <div className={styles.stickyBarCenter}>{nombreAgente}</div>
        <div className={styles.stickyBarRight}>
          <button className={styles.btnNewReunion} onClick={() => openNewReunion(selectedAgent)}>+ Nueva Reunión</button>
          <button className={styles.pdfButton} onClick={() => window.print()}>📄 PDF</button>
        </div>
      </div>

      {estadisticasLoading && !informe.agente ? (
        <div className={styles.loadingContainer}><div className={styles.spinner} /><span>Cargando informe...</span></div>
      ) : (
        <>
          {/* SECTION 1: Agent Header */}
          <div className={styles.agentHeader}>
            {totalReuniones === 0 ? (
              <div className={styles.emptyInforme}>
                <span>📂</span>
                <p>No hay reuniones registradas para este agente en {periodo}. Cargá la primera reunión con el botón de arriba.</p>
              </div>
            ) : (
              <div className={styles.agentHeaderInner}>
                <div className={styles.agentAvatarLarge}>{initialAgente}</div>
                <div className={styles.agentHeaderInfo}>
                  <div className={styles.agentHeaderName}>{nombreAgente}</div>
                  <div className={styles.agentHeaderSub}>
                    Informe anual {periodo} · {mesesActivos} meses registrados · {totalReuniones} reuniones
                  </div>
                  <div className={styles.agentBadges}>
                    <span className={`${styles.pillBadge} ${styles.pillBlue}`}>Score anual: {(kpis.scoreTotal || kpis.score || 0).toFixed(1)}</span>
                    {kpis.conversionVisitaVenta != null && (
                      <span className={`${styles.pillBadge} ${styles.pillRed}`}>Conv: {Number(kpis.conversionVisitaVenta).toFixed(1)}%</span>
                    )}
                    {(compromisos.porcentaje != null || compDados > 0) && (
                      <span className={`${styles.pillBadge} ${styles.pillGold}`}>Comp: {compPorcentaje}%</span>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {totalReuniones > 0 && (
            <>
              {/* SECTION 2: KPI Cards */}
              <div className={styles.kpiGrid}>
                <div className={styles.kpiCard}>
                  <span className={styles.kpiLabel}>Ventas cerradas</span>
                  <span className={styles.kpiValue}>{kpis.ventasCerradas ?? 0}</span>
                  <span className={styles.kpiSub}>en el año</span>
                </div>
                <div className={styles.kpiCard}>
                  <span className={styles.kpiLabel}>Monto total</span>
                  <span className={styles.kpiValue}>{formatARS(kpis.montoVendido)}</span>
                  <span className={styles.kpiSub}>facturado</span>
                </div>
                <div className={styles.kpiCard}>
                  <span className={styles.kpiLabel}>Visitas realizadas</span>
                  <span className={styles.kpiValue}>{kpis.visitasRealizadas ?? 0}</span>
                  <span className={styles.kpiSub}>conv. {kpis.conversionVisitaVenta != null ? `${Number(kpis.conversionVisitaVenta).toFixed(1)}%` : '—'}</span>
                </div>
                <div className={styles.kpiCard}>
                  <span className={styles.kpiLabel}>VRC (Tasaciones)</span>
                  <span className={styles.kpiValue}>{kpis.tasaciones ?? 0}</span>
                  <span className={styles.kpiSub}>{kpis.propiedadesCaptadas ?? 0} captadas</span>
                </div>
              </div>

              {/* SECTION 3: Evolución Mensual Line */}
              {evolucion.length > 0 && (
                <div className={styles.section}>
                  <h3 className={styles.sectionTitle}>Evolución Mensual</h3>
                  <div className={styles.chartContainer}>
                    <Line data={lineData} options={lineOpts} />
                  </div>
                </div>
              )}

              {/* SECTION 4: Historial de Reuniones (editable table) */}
              <div className={styles.section}>
                <h3 className={styles.sectionTitle}>Historial de Reuniones</h3>
                <div className={styles.tableContainer}>
                  <table className={styles.dataTable}>
                    <thead>
                      <tr>
                        <th>Mes</th>
                        <th>N° Reun.</th>
                        <th>Fecha</th>
                        <th>Ventas</th>
                        <th>Monto $</th>
                        <th>Visitas</th>
                        <th>VRC</th>
                        <th>Captadas</th>
                        <th>Posteos</th>
                        <th>Conv.</th>
                        <th>Score</th>
                        <th>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {historial.length === 0 ? (
                        <tr><td colSpan={12} style={{ textAlign: 'center', padding: 24, color: '#6B6B8A' }}>Sin reuniones individuales registradas</td></tr>
                      ) : historial.map((r) => (
                        <tr key={r.id}>
                          <td style={{ fontWeight: 500 }}>{r.mes}</td>
                          <td><span className={styles.numPill}>{r.numeroReunion}</span></td>
                          <td>{formatDate(r.fecha)}</td>
                          <td>{r.ventasCerradas ?? 0}</td>
                          <td>{formatARS(r.montoVendido)}</td>
                          <td>{r.visitasRealizadas ?? 0}</td>
                          <td>{r.tasaciones ?? 0}</td>
                          <td>{r.propiedadesCaptadas ?? 0}</td>
                          <td>{r.posteosIG ?? 0}</td>
                          <td>{r.conversionVisitaVenta != null ? `${Number(r.conversionVisitaVenta).toFixed(1)}%` : '—'}</td>
                          <td style={{ fontWeight: 700 }}>{(r.score || 0).toFixed(1)}</td>
                          <td>
                            <button className={styles.actionIcon} title="Editar" onClick={() => openEditReunion(r)}>✏️</button>
                            <button className={styles.actionIcon} title="Eliminar"
                              onClick={() => setConfirmDelete({ id: r.id, label: `Reunión N° ${r.numeroReunion} de ${r.mes}` })}>🗑️</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* SECTION 5: Tabla de métricas agrupadas mes a mes */}
              {evolucion.length > 0 && (
                <div className={styles.section}>
                  <h3 className={styles.sectionTitle}>Métricas Agrupadas por Mes</h3>
                  <div className={styles.tableContainer}>
                    <table className={styles.dataTable}>
                      <thead>
                        <tr>
                          <th>Mes</th>
                          <th>Reun.</th>
                          <th>Ventas</th>
                          <th>Monto $</th>
                          <th>Visitas</th>
                          <th>VRC</th>
                          <th>Captadas</th>
                          <th>Posteos</th>
                          <th>Conv.</th>
                          <th>Comp.</th>
                          <th>Score</th>
                        </tr>
                      </thead>
                      <tbody>
                        {evolucion.map((e, i) => (
                          <tr key={i}>
                            <td style={{ fontWeight: 500 }}>{e.mes}</td>
                            <td>{e.reuniones ?? '—'}</td>
                            <td>{e.ventasCerradas ?? '—'}</td>
                            <td>{formatARS(e.montoVendido)}</td>
                            <td>{e.visitasRealizadas ?? '—'}</td>
                            <td>{e.tasaciones ?? '—'}</td>
                            <td>{e.propiedadesCaptadas ?? '—'}</td>
                            <td>{e.posteosIG ?? '—'}</td>
                            <td>{e.conversionVisitaVenta != null ? `${Number(e.conversionVisitaVenta).toFixed(1)}%` : '—'}</td>
                            <td>{e.cumplimientoCompromisos != null ? `${Number(e.cumplimientoCompromisos).toFixed(1)}%` : '—'}</td>
                            <td style={{ fontWeight: 700, color: '#2E2F8F' }}>{(e.score || 0).toFixed(1)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* SECTION 6: Mejor / Peor Mes */}
              {(mejorMes || peorMes) && evolucion.length > 1 && (
                <div className={styles.mejorPeorGrid}>
                  {mejorMes && (
                    <div className={styles.mejorMesCard}>
                      <div className={styles.mesCardLabel} style={{ color: '#1E8449' }}>✦ Mejor mes</div>
                      <div className={styles.mesCardMes} style={{ color: '#1E8449' }}>{mejorMes.mes}</div>
                      <div className={styles.mesCardDetail}>
                        Score: {mejorMes.score?.toFixed(1)} · Ventas: {mejorMes.ventas ?? '—'} · Visitas: {mejorMes.visitas ?? '—'}
                      </div>
                    </div>
                  )}
                  {peorMes && (
                    <div className={styles.peorMesCard}>
                      <div className={styles.mesCardLabel} style={{ color: '#C0392B' }}>▼ Peor mes</div>
                      <div className={styles.mesCardMes} style={{ color: '#C0392B' }}>{peorMes.mes}</div>
                      <div className={styles.mesCardDetail}>
                        Score: {peorMes.score?.toFixed(1)} · Ventas: {peorMes.ventas ?? '—'} · Visitas: {peorMes.visitas ?? '—'}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* SECTION 7: Comparativa vs Equipo Bar */}
              {(Object.keys(compAgente).length > 0 || Object.keys(compEquipo).length > 0) && (
                <div className={styles.section}>
                  <h3 className={styles.sectionTitle}>Comparativa con el Equipo</h3>
                  <div className={styles.chartContainer}>
                    <Bar data={barData} options={barOpts} />
                  </div>
                </div>
              )}

              {/* SECTION 8: Compromisos Donut + Detail */}
              {compDados > 0 && (
                <div className={styles.section}>
                  <h3 className={styles.sectionTitle}>Compromisos Cumplidos</h3>
                  <div className={styles.compromisosLayout}>
                    <div style={{ maxWidth: 240, margin: '0 auto', position: 'relative' }}>
                      <Doughnut data={donutData} options={donutOpts} />
                      <div className={styles.donutCenter}>
                        <div className={styles.donutCenterValue}>{compPorcentaje}%</div>
                        <div className={styles.donutCenterLabel}>cumplidos</div>
                      </div>
                    </div>
                    <div className={styles.compromisosDetail}>
                      <div className={`${styles.compromisosRow} ${styles.compromisosRowGreen}`}>
                        <span>Cumplidos</span><span>{compCumplidos}</span>
                      </div>
                      <div className={`${styles.compromisosRow} ${styles.compromisosRowRed}`}>
                        <span>Pendientes</span><span>{compPendientes}</span>
                      </div>
                      <div className={`${styles.compromisosRow} ${styles.compromisosRowGray}`}>
                        <span>Total dados</span><span>{compDados}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* SECTION 9: Notas de reuniones */}
              <div className={styles.section}>
                <h3 className={styles.sectionTitle}>📝 Notas de Reuniones</h3>
                {notasPorMes.length > 0 ? (
                  notasPorMes.map((nm, i) => (
                    <div key={i}>
                      {(nm.notas || []).map((n, j) => (
                        <div key={j} className={styles.notaItem}>
                          <div className={styles.notaMeta}>{nm.mes} · Reunión {n.reunion || (j + 1)}</div>
                          <div className={styles.notaTexto}>{n.texto || n}</div>
                        </div>
                      ))}
                    </div>
                  ))
                ) : (
                  <p style={{ color: '#6B6B8A', fontSize: 13 }}>No hay notas registradas aún.</p>
                )}
              </div>

              {/* SECTION 10: Tips + Frase Motivacional */}
              {(tips.length > 0 || frase) && (
                <div className={styles.tipsDarkSection}>
                  <div className={styles.tipsDarkTitle}>✦ Tips de mejora para {nombreAgente}</div>
                  <div className={styles.tipsDarkSub}>
                    Basado en sus métricas reales · {tips.length} recomendaciones personalizadas
                  </div>
                  {tips.map((tip, i) => (
                    <div key={i} className={styles.tipCard}
                      style={{ borderColor: tip.color || '#C9A84C' }}>
                      <div className={styles.tipHeader}>
                        <span className={styles.tipIconCircle}
                          style={{ background: tip.color || '#C9A84C' }}>
                          {tip.icono || '💡'}
                        </span>
                        <span className={styles.tipTitulo}>{tip.titulo || (typeof tip === 'string' ? tip : tip.message || '')}</span>
                      </div>
                      {tip.detalle && <div className={styles.tipDetalle}>{tip.detalle}</div>}
                    </div>
                  ))}

                  {frase && (
                    <div className={styles.fraseContainer}>
                      <div className={styles.fraseLabel}>Frase del mes</div>
                      <div className={styles.fraseTexto}>"{frase}"</div>
                      <div className={styles.fraseFirma}>— Directorio BY Raíces {periodo}</div>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* Modal form */}
      <ReunionFormModal isOpen={modalOpen} onClose={() => setModalOpen(false)}
        onSave={handleSaveReunion} activeSellers={activeSellers}
        editData={editData} preSelectedSellerId={preSelectedSellerId || selectedAgent}
        loading={reunionCrudLoading} />

      {/* Toast */}
      {toast && <div className={`${styles.toast} ${toast.type === 'error' ? styles.toastError : styles.toastSuccess}`}>{toast.msg}</div>}

      {/* Confirm delete dialog */}
      {confirmDelete && (
        <div className={styles.confirmOverlay} onClick={() => setConfirmDelete(null)}>
          <div className={styles.confirmBox} onClick={e => e.stopPropagation()}>
            <p>¿Eliminar {confirmDelete.label}?</p>
            <div className={styles.confirmActions}>
              <button className={styles.btnCancel} onClick={() => setConfirmDelete(null)}>Cancelar</button>
              <button className={styles.btnSubmit} style={{ background: '#E74C3C' }}
                onClick={() => handleDeleteReunion(confirmDelete.id)}>
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReunionesStats;
